import React, { useState, useRef, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  CircularProgress,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import JobDetailContent from './JobDetailContent'
import { buildJobContextPrefix } from './helpers'
import { fetchDispatch } from 'data/helpers'
import { fetchGetJob } from 'src/redux/job'
import { fetchUpdateCandidateJob } from 'src/redux/candidateJob'
import { updateAiJobFavorite } from 'src/redux/aiJobSearch'
import { useIdentity } from 'pages/Identity/Login'
import TypingIndicator from './TypingIndicator'
import MyStethiAIIcon from '/src/components/Icons/MyStethiAIIcon'

const JobDetailPage = () => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { candidateId } = useIdentity()
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const contentRef = useRef(null)

  const rankedJobFromState = location.state?.rankedJob
  const conversationIdFromState = location.state?.conversationId

  const jobFromRedux = useSelector(state => state.job.job)
  const jobLoading = useSelector(state => state.job.loading)

  const [job, setJob] = useState(rankedJobFromState?.job || null)
  const [rankedJob] = useState(rankedJobFromState || null)

  // Inline chat state
  const [messages, setMessages] = useState([])
  const [chatConversationId, setChatConversationId] = useState(null)
  const [isTyping, setIsTyping] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [chatExpanded, setChatExpanded] = useState(true)

  // Fallback: fetch job if no route state (direct URL)
  useEffect(() => {
    if (!rankedJobFromState && id) {
      dispatch(fetchGetJob(id))
    }
  }, [id, rankedJobFromState, dispatch])

  // Update job from Redux when fetched
  useEffect(() => {
    if (!rankedJobFromState && jobFromRedux?.id && String(jobFromRedux.id) === String(id)) {
      setJob(jobFromRedux)
    }
  }, [jobFromRedux, id, rankedJobFromState])

  // Scroll chat to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [messages, isTyping])

  const handleBack = () => {
    if (location.state?.rankedJob) {
      navigate(-1)
    } else {
      navigate('/jobs')
    }
  }

  const handleFavoriteClick = ({ favorite, jobId }) => {
    dispatch(updateAiJobFavorite({ jobId, favorite }))
    dispatch(fetchUpdateCandidateJob({ candidateId, jobId, favorite }))

    setJob(previousJob => ({
      ...previousJob,
      candidateJob: {
        ...previousJob.candidateJob,
        favorite,
      },
    }))
  }

  const handleSend = (e) => {
    e.preventDefault()
    if (!prompt.trim() || isTyping) return

    const userMessage = prompt.trim()
    setMessages(previousMessages => [...previousMessages, { type: 'user', message: userMessage }])
    setPrompt('')
    setIsTyping(true)

    const abortController = new AbortController()
    const timeoutId = setTimeout(() => {
      abortController.abort()
      setMessages(previousMessages => [...previousMessages, {
        type: 'ai',
        message: "Sorry, that took too long. Please try again!",
        isError: true,
      }])
      setIsTyping(false)
    }, 20000)

    const jobContextPrefix = !chatConversationId ? buildJobContextPrefix(job) : ''
    const apiContent = jobContextPrefix ? `${jobContextPrefix}\n\n${userMessage}` : userMessage

    fetchDispatch({
      path: '/api/job_chatbot/message',
      method: 'POST',
      values: { content: apiContent, conversationId: chatConversationId },
      signal: abortController.signal,
    })
      .then(response => {
        clearTimeout(timeoutId)
        setChatConversationId(response.conversationId)
        setMessages(previousMessages => [...previousMessages, { type: 'ai', message: response.content }])
        setIsTyping(false)
      })
      .catch(error => {
        clearTimeout(timeoutId)
        if (error?.name === 'AbortError') return
        setMessages(previousMessages => [...previousMessages, { type: 'ai', message: 'Sorry, something went wrong. Please try again.', isError: true }])
        setIsTyping(false)
      })
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
  }

  if (!job && jobLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!job) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            py: 1,
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton onClick={handleBack} sx={{ color: 'text.primary' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Job Details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography color='text.secondary'>Job not found</Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Fixed back header — sits below the 56px mobile nav bar */}
      <Box
        sx={{
          position: 'fixed',
          top: '56px',
          left: 0,
          right: 0,
          zIndex: 3,
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton onClick={handleBack} sx={{ color: 'text.primary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {rankedJobFromState ? 'Back to results' : 'Job Details'}
        </Typography>
      </Box>

      {/* Scrollable content area — padded for fixed nav bar (56px) + back header (~48px) */}
      <Box
        ref={contentRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          pt: '104px',
        }}
      >
        {/* Job detail content */}
        <Box sx={{ p: 2 }}>
          <JobDetailContent
            job={job}
            rankedJob={rankedJob}
            conversationId={conversationIdFromState}
            onFavoriteClick={handleFavoriteClick}
            isMobile
          />
        </Box>

      </Box>

      {/* Chat messages — collapsible */}
      {messages.length > 0 && (
        <Box sx={{ flexShrink: 0 }}>
          {/* Toggle bar */}
          <Box
            onClick={() => setChatExpanded(previous => !previous)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 0.5,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
              backgroundColor: theme.palette.background.default,
              cursor: 'pointer',
              '&:hover': { backgroundColor: theme.palette.grey[100] },
            }}
          >
            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {chatExpanded ? 'Hide chat' : `Show chat (${messages.length})`}
            </Typography>
            {chatExpanded
              ? <ExpandMoreIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              : <ExpandLessIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            }
          </Box>

          {/* Messages */}
          {chatExpanded && (
            <Box
              sx={{
                maxHeight: '40vh',
                overflowY: 'auto',
                px: 1.5,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                backgroundColor: theme.palette.background.default,
              }}
            >
              {messages.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    flexDirection: msg.type === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  {msg.type === 'user' ? (
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                      <PersonIcon sx={{ fontSize: '1.15rem' }} />
                    </Avatar>
                  ) : (
                    <Avatar sx={{ backgroundColor: theme.palette.ai.main, width: 32, height: 32 }}>
                      <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.15rem' }} />
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      backgroundColor: msg.isError
                        ? theme.palette.error.light
                        : (msg.type === 'user'
                          ? 'rgba(42, 181, 116, 0.15)'
                          : theme.palette.grey[100]),
                      borderRadius: 2,
                      p: 1.25,
                      maxWidth: '85%',
                      border: msg.isError ? `1px solid ${theme.palette.error.main}` : 'none',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        color: msg.isError ? theme.palette.error.dark : 'inherit',
                      }}
                    >
                      {msg.message}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {isTyping && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>
      )}

      {/* Sticky bottom input bar */}
      <Box
        component='form'
        onSubmit={handleSend}
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: theme.palette.grey[300],
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
          flexShrink: 0,
          paddingBottom: `max(12px, env(safe-area-inset-bottom))`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: isTyping ? theme.palette.ai.main : 'transparent',
            transition: 'background-color 0.3s ease',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Avatar
            sx={{
              backgroundColor: theme.palette.ai.main,
              width: 32,
              height: 32,
              alignSelf: 'center',
              flexShrink: 0,
            }}
          >
            <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.15rem' }} />
          </Avatar>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size='small'
            variant='outlined'
            placeholder='Ask about this job...'
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isTyping}
            inputRef={inputRef}
            sx={{
              fontFamily: '"Kumbh Sans", sans-serif',
              '& .MuiOutlinedInput-root': {
                fontFamily: '"Kumbh Sans", sans-serif',
                fontSize: { xs: '16px', md: '0.875rem' },
                '& textarea': {
                  fontFamily: '"Kumbh Sans", sans-serif',
                  fontSize: { xs: '16px', md: '0.875rem' },
                },
                '& fieldset': {
                  borderColor: isTyping ? theme.palette.ai.main : 'rgba(0, 0, 0, 0.23)',
                  borderWidth: isTyping ? '2px' : '1px',
                },
                '&:hover fieldset': { borderColor: theme.palette.ai.main },
                '&.Mui-focused fieldset': { borderColor: theme.palette.ai.main },
              },
            }}
          />
          <Button
            type='submit'
            variant='contained'
            disabled={isTyping || !prompt.trim()}
            sx={{
              minWidth: '40px',
              height: '40px',
              p: 0,
              flexShrink: 0,
              backgroundColor: theme.palette.ai.main,
              '&:hover': { backgroundColor: theme.palette.ai.dark },
            }}
          >
            {isTyping
              ? <CircularProgress size={18} color='inherit' />
              : <SendIcon sx={{ fontSize: '1.1rem' }} />
            }
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default JobDetailPage
